import { Button, cn, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Spinner } from '@1trading/react';
import { useEffect, useState } from 'react';
import { closeOnly } from '@/components/dialogs/close-only';
import { isJustificationValid, useConfirmActionStore } from '@/components/dialogs/confirm-action';
import { JustificationField } from '@/components/dialogs/justification-field';

// Rendered once at app root (providers.tsx); driven imperatively via confirmAction()
export const ConfirmActionDialogHost = () => {
  const { request, isSubmitting, submitError, cancel, submit } = useConfirmActionStore();
  const [justification, setJustification] = useState('');

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset the field per request
  useEffect(() => {
    setJustification('');
  }, [request]);

  const requireJustification = request?.requireJustification ?? true;
  const canSubmit = !requireJustification || isJustificationValid(justification);

  return (
    <Dialog onOpenChange={closeOnly(cancel)} open={!!request}>
      {/* A long description scrolls inside the dialog so the justification and buttons stay on screen. */}
      <DialogContent className={cn('flex max-h-[90vh] flex-col', request?.size === 'wide' && 'sm:max-w-3xl')} {...(request?.description ? {} : { 'aria-describedby': undefined })}>
        <DialogHeader className="shrink-0">
          <DialogTitle>{request?.title}</DialogTitle>
          {typeof request?.description === 'string' ? <DialogDescription>{request.description}</DialogDescription> : null}
        </DialogHeader>
        {request?.description && typeof request.description !== 'string' ? (
          <DialogDescription asChild>
            <div className="-mx-1 min-h-0 flex-1 overflow-y-auto px-1">{request.description}</div>
          </DialogDescription>
        ) : null}
        {requireJustification ? (
          <JustificationField
            className="shrink-0"
            id="confirm-justification"
            justification={justification}
            onChange={(event) => setJustification(event.target.value)}
            value={justification}
          />
        ) : null}
        {submitError ? <p className="shrink-0 text-sm text-red-500">{submitError}</p> : null}
        <DialogFooter className="shrink-0">
          <Button disabled={isSubmitting} onClick={cancel} type="button" variant="outline">
            Cancel
          </Button>
          <Button
            className="gap-2"
            disabled={!canSubmit || isSubmitting}
            onClick={() => submit(justification.trim())}
            type="button"
            variant={request?.destructive ? 'destructive' : 'primary'}
          >
            {isSubmitting ? <Spinner className="size-4" /> : null}
            {request?.actionLabel ?? 'Confirm'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
ConfirmActionDialogHost.displayName = 'ConfirmActionDialogHost';
